import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { logger } from "../lib/logger";
import { AlertCircle, Loader2, Sparkles, Trash2, User, CheckCircle2, TrendingUp, Download, Zap } from "lucide-react";

const PLATFORMS = [
  { id: "tiktok", label: "TikTok" },
  { id: "instagram", label: "Instagram" },
  { id: "youtube", label: "YouTube" },
  { id: "twitter", label: "X/Twitter" },
  { id: "linkedin", label: "LinkedIn" },
];

const MIN_SAMPLES = 3;

export default function CreatorClonePage() {
  const [clone, setClone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);
  const [samples, setSamples] = useState(["", "", ""]);
  const [niche, setNiche] = useState("");
  const [platforms, setPlatforms] = useState(["tiktok"]);
  const [err, setErr] = useState("");
  const [accuracy, setAccuracy] = useState(null);
  const [sharpening, setSharpening] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: a }] = await Promise.all([
          api.get("/clone").catch(() => ({ data: null })),
          api.get("/clone/accuracy").catch(() => ({ data: null })),
        ]);
        setClone(c || null);
        if (c?.niche) setNiche(c.niche);
        if (c?.platforms) setPlatforms(c.platforms);
        setAccuracy(a || null);
      } catch (e) {
        logger.error("Load clone error", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setSample = (i, v) => setSamples((s) => s.map((x, idx) => (idx === i ? v : x)));
  const addSample = () => setSamples((s) => (s.length < 10 ? [...s, ""] : s));
  const removeSample = (i) => setSamples((s) => s.filter((_, idx) => idx !== i));
  const togglePlatform = (id) =>
    setPlatforms((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const train = async () => {
    setErr("");
    const real = samples.map((s) => s.trim()).filter(Boolean);
    if (real.length < MIN_SAMPLES) {
      setErr(`Paste at least ${MIN_SAMPLES} samples of your past content (captions, scripts, posts, tweets — anything).`);
      return;
    }
    setTraining(true);
    try {
      const { data } = await api.post("/clone/train", { samples: real, niche, platforms });
      setClone({ ...clone, profile: data.profile, active: true, sample_count: data.sample_count, niche, platforms });
      toast.success("Voice DNA extracted. Every generation now sounds like you.");
    } catch (e) {
      logger.error("Train clone error", e);
      setErr(formatError(e.response?.data?.detail) || "Training failed");
    } finally {
      setTraining(false);
    }
  };

  const toggleActive = async () => {
    try {
      const { data } = await api.post("/clone/apply", { active: !clone.active });
      setClone((c) => ({ ...c, active: data.active }));
      toast.success(data.active ? "Clone is now writing for you." : "Clone paused.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed");
    }
  };

  const destroy = async () => {
    if (!window.confirm("Delete your voice clone? This can't be undone.")) return;
    try {
      await api.delete("/clone");
      setClone(null);
      setSamples(["", "", ""]);
      toast.success("Clone deleted");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const sharpen = async () => {
    setSharpening(true);
    try {
      const { data } = await api.post("/clone/sharpen");
      setClone((c) => ({ ...c, profile: data.profile, sharpen_count: data.sharpen_count }));
      toast.success(`Clone sharpened (cycle #${data.sharpen_count}). Output should sound more like you now.`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Need more 👍 ratings first");
    } finally {
      setSharpening(false);
    }
  };

  const exportClone = async () => {
    try {
      const { data } = await api.get("/clone/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "copyforge_voice_dna.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Voice DNA downloaded. It's yours forever.");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="clone-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-brand/5 text-brand px-3 py-1 text-xs font-bold tracking-wider uppercase mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Signature Feature
          </div>
          <h1 className="text-4xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">
            Clone your voice.
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            Paste a handful of your past captions, scripts, or posts. CopyForge extracts your hook formulas,
            signature phrases, cadence, and the words you never use — then injects that DNA into every
            generation. Your AI will sound like <em>you</em>, not the internet.
          </p>
        </div>

        {clone && clone.profile ? (
          <div className="space-y-6" data-testid="clone-profile">
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-brand text-white flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg text-zinc-950 dark:text-white">Your Voice DNA</h2>
                    <p className="text-xs text-zinc-500">
                      Trained on {clone.sample_count} samples · {clone.active ? "Active" : "Paused"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleActive}
                    data-testid="clone-toggle-btn"
                    className={`px-4 py-2 text-sm font-semibold border ${clone.active ? "bg-brand text-white border-brand" : "bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700"}`}
                  >
                    {clone.active ? "Writing as you" : "Paused"}
                  </button>
                  <button
                    onClick={destroy}
                    className="p-2 text-zinc-400 hover:text-red-500 border border-zinc-200 dark:border-zinc-700"
                    data-testid="clone-delete-btn"
                    title="Delete clone"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm italic text-zinc-700 dark:text-zinc-300 border-l-2 border-brand pl-3">
                "{clone.profile.voice_summary}"
              </p>
            </div>

            {/* Accuracy + Sharpen + Export */}
            <div className="grid sm:grid-cols-3 gap-3" data-testid="clone-tools-bar">
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                <p className="text-[10px] font-bold tracking-wider uppercase text-zinc-400 mb-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Accuracy
                </p>
                {accuracy && accuracy.total > 0 ? (
                  <>
                    <p className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white">
                      {accuracy.accuracy_pct}%
                    </p>
                    <p className="text-xs text-zinc-500">
                      {accuracy.thumbs_up} 👍 · {accuracy.thumbs_down} 👎 across {accuracy.total} outputs
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-zinc-500">Rate your generations 👍 / 👎 on the Generate page to score your clone.</p>
                )}
              </div>
              <button
                onClick={sharpen}
                disabled={sharpening || !accuracy?.sharpen_ready}
                data-testid="clone-sharpen-btn"
                className="text-left bg-brand text-white p-4 hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
                title={accuracy?.sharpen_ready ? "Re-train using your top-rated outputs" : "Rate 5+ outputs 👍 first"}
              >
                <p className="text-[10px] font-bold tracking-wider uppercase opacity-80 mb-1 flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Sharpen
                </p>
                <p className="text-lg font-black tracking-tight">
                  {sharpening ? "Sharpening…" : `Sharpen (cycle #${(clone.sharpen_count || 0) + 1})`}
                </p>
                <p className="text-xs opacity-80 mt-1">
                  {accuracy?.sharpen_ready
                    ? "Re-train with your top-rated outputs."
                    : `Rate ${Math.max(5 - (accuracy?.thumbs_up || 0), 0)} more 👍 to unlock.`}
                </p>
              </button>
              <button
                onClick={exportClone}
                data-testid="clone-export-btn"
                className="text-left bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 hover:border-brand"
              >
                <p className="text-[10px] font-bold tracking-wider uppercase text-zinc-400 mb-1 flex items-center gap-1">
                  <Download className="w-3 h-3" /> Own your data
                </p>
                <p className="text-lg font-black tracking-tight text-zinc-950 dark:text-white">Export Voice DNA</p>
                <p className="text-xs text-zinc-500 mt-1">Download a JSON of your clone. Take it anywhere.</p>
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <ProfileCard title="Hook formulas" items={clone.profile.hook_formulas} />
              <ProfileCard title="Signature phrases" items={clone.profile.signature_phrases} />
              <ProfileCard title="Tone markers" items={clone.profile.tone_markers} />
              <ProfileCard title="Topic clusters" items={clone.profile.topic_clusters} />
              <ProfileCard title="Never uses" items={clone.profile.avoid_list} danger />
              <ProfileCard title="CTA style" items={clone.profile.cta_style ? [clone.profile.cta_style] : []} />
            </div>

            <div className="text-center pt-4">
              <button
                onClick={() => {
                  setSamples(["", "", ""]);
                  setClone((c) => ({ ...c, profile: null }));
                }}
                className="text-sm text-brand font-semibold hover:underline"
                data-testid="clone-retrain-btn"
              >
                Re-train with new samples →
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 space-y-5">
            <div>
              <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 block mb-2">
                Your niche <span className="text-zinc-400 font-normal">(what do you make content about?)</span>
              </label>
              <input
                data-testid="clone-niche-input"
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                placeholder="e.g. personal finance for Gen Z, ADHD productivity, SaaS founders"
                className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 block mb-2">Platforms you post on</label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => {
                  const on = platforms.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlatform(p.id)}
                      data-testid={`clone-platform-${p.id}`}
                      className={`px-3 py-1.5 text-sm font-semibold border ${on ? "bg-brand text-white border-brand" : "bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700"}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 block mb-2">
                Past content samples <span className="text-zinc-400 font-normal">(3-10 of your best-performing posts)</span>
              </label>
              <p className="text-xs text-zinc-500 mb-3">
                Paste captions, tweets, scripts, or long-form posts. The more <em>you</em> they sound, the sharper the clone.
              </p>
              <div className="space-y-2">
                {samples.map((s, i) => (
                  <div key={i} className="relative">
                    <textarea
                      data-testid={`clone-sample-${i}`}
                      value={s}
                      onChange={(e) => setSample(i, e.target.value)}
                      rows={3}
                      placeholder={`Sample ${i + 1}…`}
                      className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 pr-10 text-sm text-zinc-900 dark:text-white focus:border-brand outline-none resize-none"
                    />
                    {samples.length > MIN_SAMPLES && (
                      <button
                        type="button"
                        onClick={() => removeSample(i)}
                        className="absolute top-2 right-2 text-zinc-300 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {samples.length < 10 && (
                <button
                  type="button"
                  onClick={addSample}
                  className="mt-2 text-xs font-semibold text-brand hover:underline"
                  data-testid="clone-add-sample-btn"
                >
                  + Add another sample
                </button>
              )}
            </div>

            {err && (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {err}
              </div>
            )}

            <button
              onClick={train}
              disabled={training}
              data-testid="clone-train-btn"
              className="w-full bg-brand text-white font-semibold py-3.5 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {training ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Extracting your voice…</>
              ) : (
                <><Sparkles className="w-5 h-5" /> Train my clone</>
              )}
            </button>
            <p className="text-xs text-zinc-400 text-center flex items-center justify-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Free. Takes ~30 seconds. Runs once.
            </p>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function ProfileCard({ title, items, danger }) {
  if (!items || !items.length) return null;
  return (
    <div className={`bg-white dark:bg-zinc-900 border p-4 ${danger ? "border-red-200 dark:border-red-900" : "border-zinc-200 dark:border-zinc-800"}`}>
      <p className={`text-xs font-bold tracking-wider uppercase mb-2 ${danger ? "text-red-500" : "text-zinc-400"}`}>
        {title}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((x, i) => (
          <span
            key={i}
            className={`text-xs px-2 py-1 border ${danger ? "border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 line-through" : "border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300"}`}
          >
            {String(x)}
          </span>
        ))}
      </div>
    </div>
  );
}
